import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { api, queries, unwrap } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute("/transfers")({
  component: Transfers,
});

function Transfers() {
  const queryClient = useQueryClient();
  const me = useQuery(queries.me());
  const transfers = useQuery({ ...queries.transfers(), enabled: !!me.data });
  const act = useMutation({
    mutationFn: async ({ tid, action }: { tid: string; action: "accept" | "cancel" }) =>
      unwrap(await api.POST("/api/v1/transfers/{target}", { params: { path: { target: `${tid}:${action}` } } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfers"] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
      queryClient.invalidateQueries({ queryKey: ["airframe"] });
    },
  });
  if (me.isPending) return null;
  if (!me.data) return <p className="muted">Sign in to see your ownership transfers.</p>;
  const myId = me.data.user.id;
  // The API lists only the open transfers; a closed one is gone on the next fetch.
  const open = transfers.data ?? [];
  const done = act.data;
  return (
    <div className="space-y-5">
      <h1 className="page-title">Ownership transfers</h1>
      <p className="muted text-sm">The new owner has 7 days to take over. Until then the proposer can cancel.</p>
      <ErrorNote error={transfers.error ?? act.error} />
      {done ? (
        <p className="alert alert-ok" data-testid="transfer-done">
          {done.status === "accepted"
            ? `${done.reg} is now owned by ${done.to.name}.`
            : `The transfer of ${done.reg} was ${done.status}.`}
        </p>
      ) : null}
      {transfers.data && transfers.data.length === 0 ? <p className="muted">No open transfer involves you.</p> : null}
      <ul className="space-y-2" data-testid="transfers">
        {open.map((t) => (
          <li key={t.id} className="card flex flex-wrap items-center gap-3" data-testid="transfer" data-status={t.status}>
            <Link to="/airframes/$id" params={{ id: t.airframe_id }} className="font-semibold underline">
              {t.reg}
            </Link>
            <span className="text-sm">
              from <b>{t.from.name}</b> to <b>{t.to.name}</b>
            </span>
            <span className="muted text-xs">expires {formatDateTime(t.expires_at)}</span>
            <span className="ml-auto flex gap-2">
              {t.to.id === myId ? (
                <button type="button" className="btn btn-primary" onClick={() => act.mutate({ tid: t.id, action: "accept" })} disabled={act.isPending} data-testid="accept-transfer">
                  Take over as owner
                </button>
              ) : null}
              <button type="button" className="btn" onClick={() => act.mutate({ tid: t.id, action: "cancel" })} disabled={act.isPending} data-testid="cancel-transfer">
                {t.to.id === myId ? "Decline" : "Cancel"}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
